/**
 * Utility functions for calculating reputation impact from sentiment analysis data
 */

export interface ReputationResult {
  repDelta: number;  // -100 to +100 value of reputation change
  uiScore: number;   // 0-100 value for UI gauge
}

/**
 * Calculate reputation delta based on sentiment analysis
 * 
 * @param analysis - The sentiment analysis data
 * @returns Object with repDelta (-100 to +100) and uiScore (0 to 100) for display
 */
export function calcReputationDelta(analysis: { 
  positivePct: number;
  negativePct: number;
  confidence: string;
  delta?: string;
}): ReputationResult {
  const { positivePct: pos, negativePct: neg, confidence, delta } = analysis;

  // 1. basic weighted delta
  const rawDelta = pos - neg;                           // -100..+100
  const confScale = confidence === "High"   ? 1.0
                  : confidence === "Medium" ? 0.6
                  : 0.3;
  let repDelta = rawDelta * confScale;

  // 2. baseline-shock adjustment
  if (delta && /violates?/i.test(delta)) repDelta *= 1.2;
  if (delta && /exceeds?/i.test(delta))  repDelta *= 1.1;

  // 3. clamp and map to 0-100 gauge
  repDelta = Math.max(-100, Math.min(100, repDelta));
  const uiScore = (repDelta + 100) / 2;

  return { repDelta, uiScore };        // uiScore 0 (very bad)..100 (very good)
}

/**
 * Get reputation level information based on the calculated reputation delta
 * 
 * @param repDelta - The reputation delta value (-100 to +100)
 * @returns Object with level description and color
 */
export function getReputationLevel(repDelta: number) {
  if (repDelta >= 60) return { level: 'Strong Uplift', color: '#06D6A0' };     // Green
  if (repDelta >= 20) return { level: 'Mild Uplift', color: '#80D6A0' };       // Light Green
  if (repDelta > -20) return { level: 'Neutral', color: '#FFD166' };           // Grey/Yellow
  if (repDelta > -60) return { level: 'Mild Damage', color: '#FF9A66' };       // Orange
  return { level: 'Severe Damage', color: '#FF6B6B' };                         // Red
} 